import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";

import EvidenceTypesApi from "@/api/evidence-types";
import EvidenceAPI from "@/api/evidence";
import CustomFieldsApi from "@/api/custom-fields";
import { userApi } from "@/api";

import { CUSTOM_FIELD_CATEGORIES } from "@/pages/Settings/CustomFieldSettings/utils/customFieldConstants";
import EvidencePriorities from "@/components/Evidence/EvidencePriorities";
import CasesApi from "@/api/cases";
import {
  User,
  LocationsQuery,
  EvidenceAPI as NewEvidenceAPI,
  CustomField,
  isCustomFieldApplicableToType,
  ImportDefinition,
  ImportFieldDefinition,
  ImportBatchResult,
  resolveDateBoxValue,
  toDbDateBoxValue,
} from "@/Features";
import { useTenantSettings } from "../../../hooks/useTenantSettings";
import { TENANT_SETTINGS } from "../../../constants";

type EvidenceType = {
  evi_type: string;
  evi_type_id: number;
};

type UseEvidenceImportDefinitionOptions = {
  case_id?: number;
};

function resolveCapacityValue(value: string): number | string {
  const sizeWithUnitPattern = /^\s*(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB)\s*$/i;
  const match = value.match(sizeWithUnitPattern);

  if (!match) {
    const num = Number(value);
    return isNaN(num) ? value : num;
  }

  return Number(match[1]);
}

function resolveCapacityUnitValue(value: string): string | undefined {
  const sizeWithUnitPattern = /^\s*(\d+(?:\.\d+)?)\s*(B|KB|MB|GB|TB)\s*$/i;
  const sizeMatch = value.match(sizeWithUnitPattern);

  if (sizeMatch) {
    return sizeMatch[2].toUpperCase();
  }

  return undefined;
}

function parseTagBoxCsvValue(value: string): string[] {
  return value
    .split(/[;,|\n]/)
    .map((entry) => entry.trim())
    .filter((entry) => entry.length > 0);
}

export function useEvidenceImportDefinition(
  options: UseEvidenceImportDefinitionOptions = {}
): {
  definition: ImportDefinition | null;
  isLoading: boolean;
} {
  const { case_id } = options;
  const { tenantSettings } = useTenantSettings();

  // Check if duplicate evidence number prevention is enabled
  const preventDuplicates = tenantSettings?.find(
    (setting: any) =>
      setting.setting === TENANT_SETTINGS.PREVENT_DUPLICATE_EVIDENCE
  )?.enabled;

  const { data: locationData, isLoading: isLocationLoading } =
    LocationsQuery.useGetLocations({
      include_groups: false,
      include_meta_locations: true,
    });

  const results = useQueries({
    queries: [
      {
        queryKey: ["evidence:types"],
        queryFn: () => EvidenceTypesApi.getEvidenceTypes(),
        throwOnError: true,
      },
      {
        queryKey: [
          "evidence:customFields",
          { category: CUSTOM_FIELD_CATEGORIES.EVIDENCE },
        ],
        queryFn: () =>
          CustomFieldsApi.getCustomFields({
            category: CUSTOM_FIELD_CATEGORIES.EVIDENCE,
          }),
        throwOnError: true,
      },
      {
        queryKey: ["users", "list"],
        queryFn: () =>
          userApi.getUsers({
            include_observers: false,
            include_inactive: false,
          }),
        throwOnError: true,
      },
      {
        queryKey: ["cases", "case-numbers"],
        queryFn: () => CasesApi.getCaseNumbers(),
        throwOnError: true,
      },
      {
        queryKey: ["evidence", "evidence-numbers"],
        queryFn: () => NewEvidenceAPI.getEvidenceNumbers(),
      },
    ],
  });

  const evidenceTypes: EvidenceType[] = useMemo(
    () => results[0]?.data || [],
    [results]
  );
  const customFields: CustomField[] = useMemo(
    () =>
      results[1]?.data?.filter?.((field: CustomField) => field.enabled === 1) ||
      [],
    [results]
  );
  const users: User[] = useMemo(() => results[2]?.data || [], [results]);
  const caseNumbers: { case_number?: string; case_id: number }[] = useMemo(
    () => results[3]?.data || [],
    [results]
  );
  const evidenceNumbersData: { evidence_number: string }[] = useMemo(
    () => results[4]?.data?.data || [],
    [results]
  );

  const isLoading = results.some((r) => r.isLoading) || isLocationLoading;

  const definition = useMemo<ImportDefinition | null>(() => {
    if (isLoading) return null;

    const evidenceTypeOptions = evidenceTypes.map((t) => ({
      value: t.evi_type,
      label: t.evi_type,
    }));

    const userOptions = users.map((user) => ({
      value: user.user_id,
      label: user.full_name,
      data: user,
    }));

    const priorityOptions = EvidencePriorities.map((p) => ({
      value: p.priority_id,
      label: p.priority_name,
    }));

    const locationOptions = locationData?.map((location) => ({
      value: location.location_id,
      label: location.location_name,
      data: location,
    }));

    const capacityUnitOptions = [
      { value: "KB", label: "KB" },
      { value: "MB", label: "MB" },
      { value: "GB", label: "GB" },
      { value: "TB", label: "TB" },
    ];
    const caseNumberByIdCache = new Map<number, string>();

    const caseNumberMap = new Map<string, number>();
    for (const c of caseNumbers) {
      if (c.case_number && typeof c.case_id === "number") {
        caseNumberMap.set(String(c.case_number).toLowerCase(), c.case_id);
        caseNumberByIdCache.set(c.case_id, String(c.case_number));
      }
    }

    // Keep the DB set stable inside useMemo — never mutated after construction
    const evidenceNumbersSet = new Set<string>(
      evidenceNumbersData
        .filter((e) => e.evidence_number)
        .map((e) => e.evidence_number.trim().toLowerCase())
    );

    // Resolved item_type is the canonical evi_type string. Custom field
    // applicability uses numeric type ids, so we need a name->id lookup.
    const evidenceTypeIdByName = new Map(
      evidenceTypes.map((t) => [t.evi_type.toLowerCase(), t.evi_type_id])
    );
    const getRowEvidenceTypeId = (
      row: Record<string, unknown>
    ): number | undefined => {
      const value = row.item_type;
      if (typeof value !== "string" || !value) return undefined;
      return evidenceTypeIdByName.get(value.toLowerCase());
    };

    // Core evidence fields
    const fields: ImportFieldDefinition[] = [
      {
        key: "case_id",
        label: "Case Number",
        type: "select",
        enabled: !case_id,
        required: case_id === undefined,
        description:
          "This is the case number of the case to import the evidence into.",
        aliases: ["case id", "case", "case number"],
        displayValue: (value) => {
          if (value === null || value === undefined || value === "")
            return value;
          const normalizedId =
            typeof value === "number" ? value : Number(String(value));
          if (Number.isNaN(normalizedId)) return value;
          return caseNumberByIdCache.get(normalizedId) ?? value;
        },
        validate: (_val, _row, rawValue) => {
          if (rawValue === undefined) return null;
          if (!rawValue || !rawValue.trim()) return "Case number is required";
          if (_val === null || _val === undefined)
            return `No case match for "${rawValue.trim()}"`;
          return null;
        },
        resolver: async (val) => {
          if (case_id) {
            return case_id;
          }

          const caseNumber = String(val).trim();
          if (!caseNumber) return undefined;

          return caseNumberMap.get(caseNumber.toLowerCase());
        },
      },
      {
        key: "evidence_number",
        label: "Evidence Number",
        type: "text",
        description:
          "Unique ID for this evidence. Leave blank to auto-generate.",
        aliases: ["evidence_number", "item_number", "evidence #", "evi #"],
        validate: (_val, _row, rawValue) => {
          if (!rawValue || !rawValue.trim()) return null;

          // If the tenant setting to prevent duplicate evidence numbers is enabled, check if the evidence number already exists in the system (case-insensitive)
          if (
            preventDuplicates &&
            evidenceNumbersSet.has(String(rawValue).trim().toLowerCase())
          ) {
            return `Evidence number "${rawValue.trim()}" already exists`;
          }

          // Add to set to prevent duplicates within the same import batch, even if they don't exist in the system yet
          evidenceNumbersSet.add(String(rawValue).trim().toLowerCase());
          return null;
        },
      },
      {
        key: "item_type",
        label: "Evidence Type",
        type: "select",
        description: "Type of evidence (e.g., Mobile Device, Computer, etc.)",
        aliases: ["type", "evidence_type", "evi_type", "item type"],
        options: evidenceTypeOptions,
        resolver: (val) => {
          const lower = val.toLowerCase().trim();
          const match = evidenceTypes.find(
            (t) => t.evi_type.toLowerCase() === lower
          );
          return match ? match.evi_type : undefined;
        },
      },
      {
        key: "manufacturer",
        label: "Brand / Manufacturer",
        type: "text",
        description: "Brand or manufacturer of the item",
        aliases: ["brand", "make", "item brand", "provider"],
      },
      {
        key: "model_name",
        label: "Item Name",
        type: "text",
        description: "Model name of the device",
        aliases: ["name", "item name", "device name", "model"],
      },
      {
        key: "model_number",
        label: "Model Number",
        type: "text",
        description: "Model number for this item",
        aliases: ["model_number", "model #", "model"],
      },
      {
        key: "serial_number",
        label: "Unique Identifier",
        type: "text",
        description: "Serial number or unique identifier",
        aliases: [
          "serial",
          "serial number",
          "serial #",
          "unique id",
          "unique identifier",
        ],
      },
      {
        key: "capacity",
        label: "Size",
        type: "number",
        description: "Size/capacity of the item",
        aliases: ["size", "storage", "capacity"],
        resolver: (val) => resolveCapacityValue(val),
      },
      {
        key: "capacity_unit",
        label: "Size Unit",
        type: "select",
        description: "Unit for size (KB, MB, GB, TB)",
        aliases: ["size unit", "unit", "capacity unit", "size", "capacity"],
        options: capacityUnitOptions,
        // defaultValue: "GB",
        resolver: (val) => {
          const extractedUnit = resolveCapacityUnitValue(val);
          if (extractedUnit) {
            return extractedUnit;
          }

          const upper = val.toUpperCase().trim();
          const match = capacityUnitOptions.find((o) => o.value === upper);
          return match ? match.value : undefined;
        },
      },
      {
        key: "assigned_user_id",
        label: "Assigned User",
        type: "select",
        description: "User assigned to this evidence",
        aliases: ["assigned user", "assigned to", "assigned to user"],
        options: userOptions,
        validate: (_val, _row, rawValue) => {
          if (!rawValue) return null;
          const lower = rawValue.toLowerCase().trim();

          // Only flag ambiguity when matching by name. user_id and email
          // are unique, so they can't produce multi-matches.
          const numericId = Number(rawValue);
          const matchesById =
            !Number.isNaN(numericId) &&
            userOptions?.some((o) => o.value === numericId);
          const matchesByEmail = userOptions?.some(
            (o) => o.data?.email?.toLowerCase() === lower
          );
          if (matchesById || matchesByEmail) return null;

          const nameMatches = userOptions?.filter(
            (o) => o.label.toLowerCase() === lower
          );
          if (nameMatches && nameMatches.length > 1) {
            return "Multiple users found for the same name";
          }
          return null;
        },
        resolver: (val) => {
          const lower = val.toLowerCase().trim();

          // Match by user_id, email, or full name
          const match = userOptions?.find(
            (o) =>
              o.value === Number(val) ||
              o.data?.email?.toLowerCase() === lower ||
              o.label.toLowerCase() === lower
          );
          return match ? match.value : undefined;
        },
      },
      {
        key: "location_id",
        label: "Location",
        type: "select",
        description: "Location of this evidence",
        aliases: ["location", "evidence location", "current location"],
        options: locationOptions,
        validate: (_val, _row, rawValue) => {
          if (!rawValue) return null;
          const lower = rawValue.toLowerCase().trim();
          // Ensure that the location only matches one location
          const matches = locationOptions?.filter(
            (o) => o.label.toLowerCase() === lower
          );
          if (matches?.length && matches.length > 1) {
            return "Multiple locations found for the same name";
          }
          return null;
        },
        resolver: (val) => {
          const lower = val.toLowerCase().trim();
          const match = locationOptions?.find(
            (o) => o.label.toLowerCase() === lower
          );
          return match ? match.value : undefined;
        },
      },
      {
        key: "priority_id",
        label: "Priority",
        type: "select",
        description: "Priority level of this evidence",
        aliases: ["priority", "evidence priority"],
        options: priorityOptions,
        resolver: (val) => {
          const lower = val.toLowerCase().trim();
          const match = EvidencePriorities.find(
            (p) => p.priority_name.toLowerCase() === lower
          );
          return match ? match.priority_id : undefined;
        },
      },
      {
        key: "description",
        label: "Description",
        type: "text",
        description: "Brief description of this evidence item",
        aliases: ["description", "notes", "details"],
      },
    ];

    // Add custom fields dynamically
    for (const cf of customFields) {
      const fieldType = mapCustomFieldEditorType(cf.editor_type);

      const fieldDef: ImportFieldDefinition = {
        key: `custom_attribute_${cf.field_id}`,
        label: cf.field_name,
        type: fieldType,
        description: cf.description || undefined,
        required: cf.is_required === 1,
        aliases: [cf.field_name.toLowerCase()],
        // Type-aware applicability: fields with empty/null type_ids are
        // applies-to-all (always true); fields restricted to specific evidence
        // types only apply when the row's resolved item_type matches.
        isApplicable: (row) =>
          isCustomFieldApplicableToType(cf, getRowEvidenceTypeId(row)),
      };

      if (
        (cf.editor_type === "dropDown" || cf.editor_type === "tagBox") &&
        cf.options
      ) {
        fieldDef.options = cf.options.map((opt) => ({
          value: opt,
          label: opt,
        }));
      }

      if (cf.editor_type === "tagBox" && cf.options) {
        const optionLookup = new Map<string, string>();
        cf.options.forEach((opt) => {
          optionLookup.set(opt.toLowerCase(), opt);
        });

        fieldDef.resolver = (val) => {
          const tokens = parseTagBoxCsvValue(String(val));
          if (tokens.length === 0) return [];

          const resolvedValues: string[] = [];
          for (const token of tokens) {
            const resolvedOption = optionLookup.get(token.toLowerCase());
            if (resolvedOption !== undefined) {
              resolvedValues.push(resolvedOption);
            }
          }

          return Array.from(new Set(resolvedValues));
        };

        fieldDef.warn = (_value, _row, rawValue) => {
          if (!rawValue) return null;

          const tokens = parseTagBoxCsvValue(rawValue);
          const unmatched = tokens.filter(
            (token) => !optionLookup.has(token.toLowerCase())
          );
          if (unmatched.length === 0) return null;

          return `The following values do not match any options for ${
            cf.field_name
          } and will not be imported: ${unmatched.join(", ")}`;
        };

        fieldDef.displayValue = (value) => {
          if (!Array.isArray(value)) return value;
          return value.join(", ");
        };
      }

      if (cf.editor_type === "dateBox") {
        fieldDef.resolver = (val) => resolveDateBoxValue(String(val));
      }

      fields.push(fieldDef);
    }

    const onImport = async (
      records: Record<string, unknown>[]
    ): Promise<ImportBatchResult> => {
      let successful = 0;
      let failed = 0;
      const errors: { row: number; message: string }[] = [];

      for (let i = 0; i < records.length; i++) {
        try {
          const record = { ...records[i] };

          // Inject case_id if provided
          if (case_id) {
            record.case_id = case_id;
          }

          // Build custom_attributes only from fields that apply to this row's
          // resolved evidence type. Strip any non-applicable custom_attribute_*
          // keys so we never POST irrelevant attributes for the row's type.
          const rowTypeId = getRowEvidenceTypeId(record);
          const customAttributes: {
            field_id: number;
            value: unknown;
          }[] = [];
          for (const cf of customFields) {
            const key = `custom_attribute_${cf.field_id}`;
            if (isCustomFieldApplicableToType(cf, rowTypeId)) {
              if (key in record) {
                customAttributes.push({
                  field_id: cf.field_id,
                  value:
                    cf.editor_type === "dateBox"
                      ? toDbDateBoxValue(record[key])
                      : record[key],
                });
              }
            }
            delete record[key];
          }
          if (customAttributes.length > 0) {
            record.custom_attributes = customAttributes;
          }

          await EvidenceAPI.createEvidence(record);
          successful++;
        } catch (err) {
          failed++;
          errors.push({
            row: i,
            message:
              err instanceof Error ? err.message : "Failed to create evidence",
          });
        }
      }

      return { successful, failed, errors };
    };

    return {
      id: "evidence",
      title: "Import Evidence from CSV",
      description:
        "Upload a CSV file to bulk import evidence items. Map your CSV columns to evidence fields, preview the data, and import.",
      fields,
      onImport,
      batchSize: 5,
    };
  }, [
    isLoading,
    case_id,
    locationData,
    evidenceTypes,
    customFields,
    users,
    caseNumbers,
    preventDuplicates,
    evidenceNumbersData,
  ]);

  return { definition, isLoading };
}

function mapCustomFieldEditorType(
  editorType: CustomField["editor_type"]
): ImportFieldDefinition["type"] {
  switch (editorType) {
    case "textBox":
      return "text";
    case "dropDown":
      return "select";
    case "dateBox":
      return "date";
    case "tagBox":
      return "select";
    default:
      return "text";
  }
}
